<script lang="ts" setup>
import { breakpointsTailwind, useBreakpoints } from '@vueuse/core';
import isEqual from 'lodash/isEqual';
import uniqWith from 'lodash/unionWith';
import { computed, reactive, ref, watch } from 'vue';

import { getRecentReviews } from '@/api/reviews';
import Skeleton from '@/components/base/Skeleton.vue';
import ThemedButton from '@/components/base/ThemedButton.vue';
import BaseBodyText from '@/components/base/typography/BaseBodyText.vue';
import Header4 from '@/components/base/typography/Header4.vue';
import UnstyledButton from '@/components/base/UnstyledButton.vue';
import RatingStars from '@/components/pdp/reviews/RatingStars.vue';
import RatingTable from '@/components/pdp/reviews/RatingTable.vue';
import Review from '@/components/pdp/reviews/Review.vue';
import ReviewSkeleton from '@/components/pdp/reviews/ReviewSkeleton.vue';
import ReviewsPopper from '@/components/reviews/ReviewsPopper.vue';
import { useCallback } from '@/composables/useCallback';
import { pluralizeBy } from '@/filters/pluralize';
import { ApprovedReview, ApprovedReviews, RatingTier } from '@/lib/reviews';

const props = defineProps<{
  productKey: string;
  reviews: ApprovedReviews;
  showReviewPopper?: boolean;
}>();

defineEmits(['review-submitted', 'write-a-review']);

const isMobile = useBreakpoints(breakpointsTailwind).smaller('lg');

const displayReviewsContent = () =>
  uniqWith(
    [
      props.reviews.featuredReview,
      props.reviews.mostRecentTopRatedReview,
      ...props.reviews.mostRecentTopRatedReviewsOver100Chars,
      ...props.reviews.reviews,
    ].filter((review) => review !== null) as ApprovedReview[],
    isEqual,
  );

const reviewByRating = reactive<
  Record<RatingTier | 'all', Pick<ApprovedReviews, 'reviews' | 'totalRatings' | 'totalReviews'>>
>({
  all: {
    reviews: displayReviewsContent(),
    totalRatings: props.reviews.totalRatings,
    totalReviews: props.reviews.totalReviews,
  },
  1: { reviews: [], totalRatings: 0, totalReviews: 0 },
  2: { reviews: [], totalRatings: 0, totalReviews: 0 },
  3: { reviews: [], totalRatings: 0, totalReviews: 0 },
  4: { reviews: [], totalRatings: 0, totalReviews: 0 },
  5: { reviews: [], totalRatings: 0, totalReviews: 0 },
});

const selectedRating = ref<RatingTier>();
const selectedReview = computed(() => reviewByRating[selectedRating.value ?? 'all']);

const offset = computed(() => {
  const { featuredReview, mostRecentTopRatedReview, mostRecentTopRatedReviewsOver100Chars } =
    props.reviews;

  let count = selectedReview.value.reviews.length;

  if (!selectedRating.value) {
    if (featuredReview) count -= 1;
    if (mostRecentTopRatedReview) count -= 1;
    count -= mostRecentTopRatedReviewsOver100Chars.length;
  }
  return count;
});
const limit = 10;

const loadMoreReviews = useCallback(async () => {
  const newReviews = await getRecentReviews(
    props.productKey,
    selectedRating.value,
    offset.value,
    limit,
  );

  const key = selectedRating.value ?? 'all';
  reviewByRating[key].reviews = [...reviewByRating[key].reviews, ...newReviews.data.data.reviews];
  reviewByRating[key].totalRatings = newReviews.data.data.totalRatings;
  reviewByRating[key].totalReviews = newReviews.data.data.totalReviews;
});

const selectRating = useCallback(async (newRating: RatingTier) => {
  selectedRating.value = newRating;

  if (!reviewByRating[newRating].reviews.length) {
    const newReviews = await getRecentReviews(props.productKey, newRating, 0, limit);

    const { reviews, totalRatings, totalReviews } = newReviews.data.data;
    reviewByRating[newRating] = { reviews, totalRatings, totalReviews };
  }
});

const clearRatingFilter = () => {
  selectedRating.value = undefined;
};

watch(
  () => props.reviews,
  () => {
    clearRatingFilter();
    reviewByRating.all.reviews = displayReviewsContent();
    reviewByRating.all.totalRatings = props.reviews.totalRatings;
    reviewByRating.all.totalReviews = props.reviews.totalReviews;
    const keys: RatingTier[] = [1, 2, 3, 4, 5];
    keys.forEach((key: RatingTier) => {
      reviewByRating[key].reviews = [];
      reviewByRating[key].totalRatings = 0;
      reviewByRating[key].totalReviews = 0;
    });
  },
);

const quantity = computed(() => props.reviews.totalRatings);
const displayText = computed(() => pluralizeBy('Rating', quantity.value));
</script>

<template>
  <div class="flex flex-col">
    <div class="flex flex-col justify-between md:flex-row" data-test="ratings-reviews-section">
      <div class="items-center hidden lg:visible lg:flex">
        <Header4 headerTag="h3">Customer Reviews</Header4>
        <UnstyledButton
          v-if="selectedRating"
          class="ml-24"
          aria-label="View all reviews"
          @click="clearRatingFilter"
          data-promo="1"
          data-promo-name="Reviews"
          data-promo-creative="Review Distribution: View All"
        >
          <BaseBodyText class="underline text-nuts-cyan-800">View all</BaseBodyText>
        </UnstyledButton>
      </div>
      <component
        :is="!isMobile && showReviewPopper ? ReviewsPopper : 'div'"
        location="pdp"
        v-slot="popperProps"
      >
        <ThemedButton
          class="hidden mt-3 md:mt-0 md:block md:visible"
          size="small"
          theme="green"
          @click="
            $emit('write-a-review');
            popperProps?.dismiss();
          "
          data-promo="1"
          data-promo-name="Write a review"
          data-promo-creative="PDP"
        >
          <BaseBodyText>Write a review</BaseBodyText>
        </ThemedButton>
      </component>
    </div>
    <div class="flex items-center mt-1 md:mt-6" data-test="number-of-reviews">
      <Header4 class="mt-0.5" headerTag="span">{{ reviews.averageRating.toFixed(1) }}</Header4>
      <RatingStars class="ml-1.5" :rating="reviews.averageRating" :size="[16, 24]" />
      <span class="ml-1.5 text-md md:text-xl font-sofia-pro">
        <strong class="font-semibold">{{ quantity.toLocaleString() }}</strong> {{ displayText }}
      </span>
    </div>
    <BaseBodyText v-if="!reviews.totalReviews" class="mt-4 text-neutral-500">
      This product currently doesn't have any written reviews yet
    </BaseBodyText>
    <div v-else>
      <RatingTable
        class="mt-2 lg:mt-5"
        :reviews="reviews"
        :selectedRating="selectedRating"
        @selectRating="selectRating.execute"
      />
      <ThemedButton
        class="w-full mt-6 md:hidden"
        size="small"
        theme="green"
        @click="$emit('write-a-review')"
        data-promo="1"
        data-promo-name="Write a review"
        data-promo-creative="PDP"
      >
        <BaseBodyText>Write a review</BaseBodyText>
      </ThemedButton>
      <div
        v-if="selectedRating && !selectRating.isPending"
        class="flex justify-between mt-4 md:justify-start"
      >
        <BaseBodyText class="text-neutral-600" aria-live="polite">
          Showing {{ selectedReview.reviews.length }} of {{ selectedReview.totalReviews }} reviews
          with "{{ selectedRating }} {{ pluralizeBy('star', selectedRating) }} ratings"
          <br class="md:hidden" />
          ({{ selectedReview.totalRatings }}
          total ratings)
        </BaseBodyText>
        <UnstyledButton
          class="ml-3"
          aria-label="Clear reviews filters"
          @click="clearRatingFilter"
          data-test="clear-rating-filter"
          data-promo="1"
          data-promo-name="Reviews"
          data-promo-creative="Review Distribution: Clear"
        >
          <BaseBodyText class="underline text-nuts-cyan-800">Clear</BaseBodyText>
        </UnstyledButton>
      </div>
      <div id="reviews-details" class="mt-3 lg:mt-6">
        <template v-if="selectRating.isPending">
          <Skeleton class="h-6 mb-4 w-72 lg:w-80" />
          <ReviewSkeleton
            v-for="item in [1, 2, 3, 4]"
            class="py-3 border-t border-solid lg:py-6 border-neutral-200"
            :key="`review-skeleton-${item}`"
          />
        </template>
        <template v-else>
          <Review
            v-for="review in selectedReview.reviews"
            class="py-3 border-t border-solid lg:py-6 border-neutral-200"
            :featured="isEqual(review, reviews.featuredReview)"
            :key="review.id"
            :review="review"
          />
        </template>
      </div>
      <div class="flex justify-center mt-4 space-x-2 md:hidden">
        <ThemedButton
          v-if="selectedReview.reviews.length < selectedReview.totalReviews"
          class="whitespace-nowrap"
          data-promo="1"
          data-promo-name="Reviews"
          data-promo-creative="Listing: Load more reviews"
          data-test="load-more-reviews"
          theme="white"
          :isLoading="loadMoreReviews.isPending"
          @click="loadMoreReviews.execute"
        >
          Load more reviews
        </ThemedButton>
        <slot name="footer-mobile-secondary-btn" />
      </div>
      <BaseBodyText v-if="!selectedRating" class="pb-2 mt-4 md:hidden">
        Showing {{ selectedReview.reviews.length }} of {{ selectedReview.totalReviews }} ratings
        with reviews
      </BaseBodyText>
      <div
        class="hidden pt-4 mt-2 text-center border-t border-solid md:block md:visible grow border-neutral-200"
      >
        <ThemedButton
          v-if="selectedReview.reviews.length < selectedReview.totalReviews"
          class="mt-6 center-block"
          data-test="pdp-reviews-load-more-reviews"
          data-promo="1"
          data-promo-name="Reviews"
          data-promo-creative="Listing: Load more reviews"
          theme="white"
          :isLoading="loadMoreReviews.isPending"
          @click="loadMoreReviews.execute"
        >
          Load more reviews
        </ThemedButton>
        <BaseBodyText class="mt-2">
          Showing {{ selectedReview.reviews.length }} of {{ selectedReview.totalReviews }} ratings
          with reviews
        </BaseBodyText>
      </div>
    </div>
  </div>
</template>
